import { painelAdministrativoApi as api } from '../../painel-administrativo.api';
import { dica } from '../../painel-administrativo.dica';
import { dialogo } from '../../painel-administrativo.dialogo';
import { PainelAdministrativoContexto, PainelAdministrativoModulo, PainelAdministrativoSecao } from '../../painel-administrativo.modulo';
import {
    alternarOrdenacao, badge, cabecalhoOrdenavel, ColunaOrdenavel, contemTexto, diasDesde, distintos, escaparHtml,
    formatarDataHora, formatarDuracaoMinutos, mensagem, Ordenacao, ordenarLinhas, tempoDesde
} from '../../painel-administrativo.utils';

const ROTA = 'admin_painel_administrativo_licencas_';
/** Uma volta completa do ícone do Atualizar, e o tempo mínimo que ele gira. */
const DURACAO_GIRO_MS = 800;

type Secao = 'clientes' | 'dispositivos' | 'licencas';
type Tabela = 'clientes' | 'dispositivos' | 'licencas';
type Tom = 'ok' | 'alerta' | 'erro' | 'neutro';

interface Cliente {
    tenant: number; tenant_codigo: string; tenant_nome: string; contratadas: number | null;
    atualizacao_automatica: boolean; dispositivos_recfacial: number; dispositivos_com_licenca: number;
    dispositivos_aguardando_licenca: number; saldo_contrato: number;
    /** Pares reservados ao cliente (total e livres no estoque). */
    licencas_reservadas: number; licencas_reservadas_livres: number;
}

interface ValoresLicenca {
    componentes: string[]; tipos: string[]; formatos: string[];
}

interface Resumo {
    estoque: { licencas_disponiveis: number; licencas_reservadas_disponiveis: number };
    /** O que o cadastro aceita, direto dos enums do backend. */
    valores?: ValoresLicenca;
    tenants: Cliente[];
}

interface Dispositivo {
    tenant: number; tenant_codigo: string; dispositivo: string; nome: string; deviceid: string;
    status: number | null; excluido: boolean; usarrecfacialativo: boolean; dataativacao: string;
    data_ativacao_licenca: string | null; licencas: { licenca: string; nome_arquivo: string; componente: string }[];
    situacao: 'COM_LICENCA' | 'AGUARDANDO_LICENCA' | 'LICENCA_SEM_USO_POSSIVEL';
    /** Quanto a licença aguenta sem internet (a menor do par). Nulo sem licença vinculada. */
    duracao_offline_minutos: number | null;
    /** Última consulta do app a GET /ponto/rep-mobile/licencas (a cada 20 min, quando há internet). */
    data_ultimo_contato: string | null;
}

interface Uso {
    tenant: number; dispositivo: string; ultima_marcacao_recfacial: string; marcacoes_recfacial: number;
}

interface LicencaDoPar {
    licenca: string; nome_arquivo: string; componente: string; tipo: string; formato: string; data_compra: string | null;
    duracao_offline_minutos: number;
}

/** Unidade que um dispositivo consome: uma licença CLIENT + uma MATCHER (agrupadas pelo backend). */
interface Par {
    situacao: 'DISPONIVEL' | 'VINCULADA' | 'BLOQUEADA'; ordem: number | null; completo: boolean;
    /** Cliente a quem o par está reservado (só ele usa; ele só usa os reservados). */
    tenant_reservado: number | null; tenant_reservado_codigo: string | null;
    tenant: number | null; tenant_codigo: string | null; dispositivo: string | null; dispositivo_nome: string | null;
    deviceid: string | null; data_ativacao: string | null; data_compra: string | null; licencas: LicencaDoPar[];
    /** A menor do par: é a que o app usa. */
    duracao_offline_minutos: number | null;
}

interface Historico {
    acao: string; detalhe: string | null; data_evento: string; tenant: number | null;
    tenant_codigo: string | null; dispositivo: string | null; dispositivo_nome: string | null;
}

interface ItemCadastro {
    nome_arquivo: string; conteudo: string; componente: string;
    tipo: string | null; formato: string | null;
    /** Campos que vieram do nome e ainda não foram trocados na tela (marcados "do nome"). */
    do_nome: { componente: boolean; tipo: boolean; formato: boolean };
}

/**
 * Mesma regra de PontoBundle\Service\Licencas\NomeArquivoLicenca, só para pré-selecionar na tela (quem
 * cadastra pode trocar, e o valor escolhido prevalece no backend): palavra inteira, em qualquer posição e
 * caixa; componente aceita o prefixo "Face" e "cliente" não é CLIENT.
 *   12_Mobile_FaceMatcher_-1223646781_internet_license.lic → MATCHER, MOBILE, INTERNET
 * Campo ambíguo ou ausente fica nulo. As listas vêm do backend (resumo.valores); estas são só o reserva
 * até o resumo carregar.
 */
const VALORES_PADRAO: ValoresLicenca = {
    componentes: ['CLIENT', 'MATCHER'],
    tipos: ['MOBILE', 'WEB', 'PC', 'SERVER'],
    formatos: ['SERIAL', 'INTERNET'],
};

function palavraUnica(nome: string, valores: string[], prefixo = ''): string | null {
    const encontrados = valores.filter(valor => new RegExp(`(^|[^a-z])${prefixo}${valor.toLowerCase()}(?![a-z])`).test(nome));

    return encontrados.length === 1 ? encontrados[0] : null;
}

function interpretarNomeLicenca(nomeArquivo: string, valores: ValoresLicenca): { componente: string | null; tipo: string | null; formato: string | null } {
    const nome = nomeArquivo.trim().replace(/\.lic$/i, '').toLowerCase();

    return {
        componente: palavraUnica(nome, valores.componentes, '(face)?'),
        tipo: palavraUnica(nome, valores.tipos),
        formato: palavraUnica(nome, valores.formatos),
    };
}

/** Mesma normalização de PontoBundle\Service\Licencas\ConteudoLicenca: a licença vai numa linha só. */
function normalizarConteudoLicenca(conteudo: string): string {
    return conteudo.replace(/^[ \t]*#[ \t]*/gm, '').replace(/[ \t]*[\r\n]+[ \t]*/g, '').trim();
}

const SITUACAO_DISPOSITIVO: { [k: string]: [string, Tom] } = {
    COM_LICENCA: ['Com licença', 'ok'],
    AGUARDANDO_LICENCA: ['Aguardando licença', 'alerta'],
    LICENCA_SEM_USO_POSSIVEL: ['Licença presa', 'erro'],
};

const SITUACAO_PAR: { [k: string]: [string, Tom] } = {
    DISPONIVEL: ['Disponível', 'ok'],
    VINCULADA: ['Em uso', 'neutro'],
    BLOQUEADA: ['Bloqueada', 'alerta'],
};

/** Padrão da Neurotec (DEFAULT de ponto.licencas.duracao_offline_minutos) e teto aceito pelo backend. */
const DURACAO_OFFLINE_PADRAO_MINUTOS = 30;
const DURACAO_OFFLINE_MAXIMA_MINUTOS = 7 * 24 * 60;

const TEXTO_TOLERANCIA_OFFLINE = 'Por quanto tempo a licença segue valendo sem internet depois da última validação na Neurotec. '
    + 'É parâmetro da licença, definido pela Neurotec ao emiti-la (30 minutos no padrão). '
    + 'Mudar aqui não muda a licença, só registra o que a Neurotec emitiu; o app recebe o valor na próxima consulta de licenças.';
const TEXTO_TOLERANCIA_LICENCAS = 'Quanto tempo a licença continua funcionando sem internet depois da última validação na Neurotec. '
    + 'Esse tempo é definido pela Neurotec em cada licença (30 minutos no padrão). Aqui ele só fica registrado, para o app mostrar '
    + 'ao operador quanto tempo resta sem internet: alterar este valor não muda a duração da licença. Se os dois arquivos do par '
    + 'tiverem valores diferentes, vale o menor, e os dois aparecem embaixo. Para corrigir o registro, selecione as licenças e use '
    + '"Tolerância offline…".';
const TEXTO_TOLERANCIA_DISPOSITIVO = 'Quanto tempo a licença continua funcionando sem internet depois da última validação na Neurotec. '
    + 'Esse tempo é definido pela Neurotec em cada licença (30 minutos no padrão) e aqui só fica registrado. Se os dois arquivos '
    + 'do par tiverem valores diferentes, aparece o menor. O registro é corrigido na grade de Licenças.';
const TEXTO_ULTIMO_CONTATO = 'Última vez que a estação falou com o servidor. Ligada, com internet e com o app aberto, ela faz isso '
    + 'a cada ~20 minutos. Se o último contato for mais antigo que a tolerância offline, a estação está sem internet, e o '
    + 'reconhecimento facial provavelmente parou, ou está fora de uso. Fica vazio enquanto a estação aguarda licença.';

const TEXTO_LICENCAS_DISPONIVEIS = 'Pares de licença (CLIENT + MATCHER) livres no estoque. Cada dispositivo usa um par.';
const TEXTO_AGUARDANDO = 'Dispositivos com reconhecimento facial ativo que não estão vinculados a nenhuma licença.';

/**
 * Módulo de licenças de reconhecimento facial: clientes (contrato), dispositivos (com a liberação
 * manual) e licenças (estoque em pares, histórico e cadastro).
 */
export class LicencasModulo implements PainelAdministrativoModulo {
    readonly icone = 'fa-id-card';

    readonly secoes: PainelAdministrativoSecao[] = [
        { codigo: 'clientes', nome: 'Clientes', icone: 'fa-building' },
        { codigo: 'dispositivos', nome: 'Dispositivos', icone: 'fa-tablet-alt' },
        { codigo: 'licencas', nome: 'Licenças', icone: 'fa-key' },
    ];

    private container: HTMLElement;
    private contexto: PainelAdministrativoContexto;
    private secao: Secao = 'clientes';
    private busca = '';
    private aviso = '';

    private resumo: Resumo | null = null;
    private dispositivos: Dispositivo[] = [];
    private pares: Par[] = [];
    private carregando = false;
    private carregado = false;
    private erro = '';

    private ordenacao: { [tabela: string]: Ordenacao | null } = { clientes: null, dispositivos: null, licencas: null };

    private dias = 15;
    private uso: { [chave: string]: Uso } | null = null;
    private calculandoUso = false;
    private erroUso = '';

    private filtroSituacaoPar = '';
    /** '' = todas; 'sem' = sem reserva; número = reservadas àquele tenant. */
    private filtroReserva = '';
    /** Pares marcados na grade de licenças (chave = ids das licenças do par). */
    private selecionados: { [chave: string]: boolean } = {};
    private historico: { licenca: LicencaDoPar; linhas: Historico[] } | null = null;
    private escutandoEsc = false;

    /** Cadastro fica dentro da seção Licenças. */
    private cadastrando = false;
    private itensCadastro: ItemCadastro[] = [];
    private dataCompra = '';
    /** Vazio = padrão da Neurotec (30 min). */
    private duracaoCadastro = '';
    private salvando = false;

    montar(container: HTMLElement, secao: string, contexto: PainelAdministrativoContexto): void {
        this.container = container;
        this.contexto = contexto;
        this.secao = secao as Secao;
        this.aviso = '';
        this.historico = null;
        this.busca = '';
        this.cadastrando = false;

        container.onclick = (evento) => this.aoClicar(evento);
        container.oninput = (evento) => this.aoDigitar(evento);
        container.onchange = (evento) => this.aoMudar(evento);

        if (!this.escutandoEsc) {
            this.escutandoEsc = true;
            document.addEventListener('keydown', (evento) => {
                if (evento.key === 'Escape' && this.historico) {
                    this.historico = null;
                    this.renderConteudo();
                }
            });
        }

        // A instância é reaproveitada entre as seções: os dados (e a última marcação calculada) ficam.
        if (this.carregado) {
            this.render();
        } else {
            this.carregar();
        }
    }

    async montarResumoInicial(container: HTMLElement): Promise<void> {
        container.innerHTML = '<div class="pa-vazio">Carregando…</div>';

        try {
            this.resumo = await api.get<Resumo>(ROTA + 'resumo');
            container.innerHTML = this.renderCards();
        } catch (e) {
            container.innerHTML = mensagem(e.message, 'erro');
        }
    }

    // ---------------------------------------------------------------- dados

    /** @param peloBotao vindo do Atualizar; quem salva algo recarrega com a tela redesenhada, para o aviso aparecer na hora. */
    private async carregar(peloBotao = false): Promise<void> {
        const atualizando = peloBotao && this.carregado;
        this.carregando = true;
        this.erro = '';

        // No Atualizar, só o botão muda (redesenhar a tela fazia tudo piscar) e o giro dura ao menos
        // uma volta: resposta rápida não dava para ver.
        let giroMinimo: Promise<void> | null = null;
        if (atualizando) {
            this.container.querySelectorAll('[data-acao="recarregar"]').forEach(botao => botao.outerHTML = this.botaoAtualizar());
            giroMinimo = new Promise(resolver => setTimeout(resolver, DURACAO_GIRO_MS));
        } else {
            this.render();
        }

        try {
            const [resumo, dispositivos, pares] = await Promise.all([
                api.get<Resumo>(ROTA + 'resumo'),
                api.get<Dispositivo[]>(ROTA + 'dispositivos'),
                api.get<Par[]>(ROTA + 'index'),
            ]);
            this.resumo = resumo;
            this.dispositivos = dispositivos;
            this.pares = pares;
            this.carregado = true;
        } catch (e) {
            this.erro = e.message;
        }

        await giroMinimo;
        this.carregando = false;
        this.render();
    }

    private async calcularUso(): Promise<void> {
        this.calculandoUso = true;
        this.erroUso = '';
        this.render();

        try {
            const resposta = await api.get<{ dias: number; dispositivos: Uso[] }>(ROTA + 'uso_dispositivos', { dias: this.dias });
            const mapa: { [chave: string]: Uso } = {};
            resposta.dispositivos.forEach(u => mapa[`${u.tenant}|${u.dispositivo}`] = u);
            this.uso = mapa;
        } catch (e) {
            this.erroUso = e.message;
        }

        this.calculandoUso = false;
        this.render();
    }

    private async reservar(): Promise<void> {
        const pares = this.paresSelecionados();
        if (!pares.length || !this.resumo) {
            return;
        }

        const clientes = this.resumo.tenants.slice().sort((a, b) => String(a.tenant_codigo).localeCompare(String(b.tenant_codigo), 'pt-BR'));
        const valores = await dialogo.formulario<{ tenant_id: number }>({
            titulo: `Reservar ${pares.length} licença(s)`,
            icone: 'fa-user-lock',
            rotuloConfirmar: 'Reservar',
            corpoHtml: `
                <label>Cliente
                    <select data-campo="tenant-reserva">
                        <option value="">Escolha…</option>
                        ${clientes.map(t => `<option value="${t.tenant}">${escaparHtml(t.tenant_codigo || String(t.tenant))} (${t.tenant})</option>`).join('')}
                    </select>
                </label>
                <p class="pa-ajuda" style="margin: 0;">Depois da reserva, <b>só esse cliente usa estas licenças</b>, e ele passa a
                    <b>usar só as licenças reservadas a ele</b>: quando acabarem, os dispositivos dele ficam aguardando licença. Licença em
                    uso continua com o dispositivo; a reserva vale quando ela voltar ao estoque.</p>
            `,
            coletar: (corpo) => {
                const escolhido = (corpo.querySelector('[data-campo="tenant-reserva"]') as HTMLSelectElement).value;
                if (!/^\d+$/.test(escolhido)) {
                    throw new Error('Escolha o cliente da reserva.');
                }

                return { tenant_id: Number(escolhido) };
            },
        });
        if (!valores) {
            return;
        }

        await this.enviarReserva(pares, valores.tenant_id);
    }

    private async removerReserva(): Promise<void> {
        const pares = this.paresSelecionados().filter(p => p.tenant_reservado !== null);
        if (!pares.length) {
            return;
        }

        const resposta = await dialogo.confirmar({
            titulo: 'Remover reserva',
            tom: 'alerta',
            pergunta: `Remover a reserva de ${pares.length} licença(s)?`,
            explicacaoHtml: '<p>As licenças voltam para o estoque geral e podem ser usadas por qualquer cliente. Cliente que ficar sem nenhuma reserva volta a usar o estoque geral.</p>',
            rotuloConfirmar: 'Remover reserva',
            iconeConfirmar: 'fa-lock-open',
        });
        if (!resposta.confirmado) {
            return;
        }

        await this.enviarReserva(pares, null);
    }

    private async enviarReserva(pares: Par[], tenantId: number | null): Promise<void> {
        try {
            const resposta = await api.post<{ total: number }>(ROTA + 'reservas', {}, {
                licencas: [].concat(...pares.map(p => p.licencas.map(l => l.licenca))),
                tenant_id: tenantId,
            });
            this.selecionados = {};
            this.aviso = tenantId !== null
                ? `${resposta.total / 2} licença(s) reservada(s) para o tenant ${tenantId}.`
                : `Reserva removida de ${resposta.total / 2} licença(s).`;
            await this.carregar();
        } catch (e) {
            await dialogo.avisar('Não foi possível alterar a reserva', e.message);
        }
    }

    /** Bloquear = licença em uso sem dispositivo: o app nunca a recebe. Por par e só de licença livre. */
    private async bloquear(): Promise<void> {
        const pares = this.paresSelecionados();
        if (!pares.length || pares.some(p => p.situacao !== 'DISPONIVEL')) {
            return;
        }

        const valores = await dialogo.formulario<{ motivo: string }>({
            titulo: `Bloquear ${pares.length} licença(s)`,
            icone: 'fa-ban',
            rotuloConfirmar: 'Bloquear',
            corpoHtml: `
                <label>Motivo
                    <textarea data-campo="motivo-bloqueio" maxlength="500" placeholder="Ex.: licença de testes"></textarea>
                </label>
                <p class="pa-ajuda" style="margin: 0;">A licença sai de circulação: <b>nenhum dispositivo a recebe</b> até ser
                    desbloqueada. O motivo fica no histórico da licença.</p>
            `,
            coletar: (corpo) => {
                const motivo = (corpo.querySelector('[data-campo="motivo-bloqueio"]') as HTMLTextAreaElement).value.trim();
                if (!motivo) {
                    throw new Error('Informe o motivo do bloqueio.');
                }

                return { motivo };
            },
        });
        if (!valores) {
            return;
        }

        await this.enviarBloqueio('bloqueios', pares, valores.motivo, 'bloqueada(s)');
    }

    private async desbloquear(): Promise<void> {
        const pares = this.paresSelecionados();
        if (!pares.length || pares.some(p => p.situacao !== 'BLOQUEADA')) {
            return;
        }

        const resposta = await dialogo.confirmar({
            titulo: 'Desbloquear licenças',
            tom: 'alerta',
            pergunta: `Desbloquear ${pares.length} licença(s)?`,
            explicacaoHtml: '<p>As licenças voltam para o estoque e podem ser entregues ao próximo dispositivo que pedir.</p>',
            campoTexto: { rotulo: 'Motivo (opcional)', placeholder: 'Ex.: fim dos testes' },
            rotuloConfirmar: 'Desbloquear',
            iconeConfirmar: 'fa-unlock',
        });
        if (!resposta.confirmado) {
            return;
        }

        await this.enviarBloqueio('desbloqueios', pares, resposta.texto || null, 'desbloqueada(s)');
    }

    private async enviarBloqueio(rota: 'bloqueios' | 'desbloqueios', pares: Par[], motivo: string | null, feito: string): Promise<void> {
        try {
            const resposta = await api.post<{ total: number }>(ROTA + rota, {}, {
                licencas: [].concat(...pares.map(p => p.licencas.map(l => l.licenca))),
                motivo,
            });
            this.selecionados = {};
            this.aviso = `${resposta.total / 2} licença(s) ${feito}.`;
            await this.carregar();
        } catch (e) {
            await dialogo.avisar(rota === 'bloqueios' ? 'Não foi possível bloquear' : 'Não foi possível desbloquear', e.message);
        }
    }

    private async definirToleranciaOffline(): Promise<void> {
        const pares = this.paresSelecionados();
        if (!pares.length) {
            return;
        }

        // cada arquivo, não a menor de cada par: salvar grava todos, e um par divergente tem de aparecer como tal
        const duracoes = distintos(([] as number[]).concat(...pares.map(p => p.licencas.map(l => l.duracao_offline_minutos))));
        const atual = duracoes.length === 1 ? duracoes[0] : null;
        const valores = await dialogo.formulario<{ duracao_offline_minutos: number }>({
            titulo: `Tolerância offline de ${pares.length} licença(s)`,
            icone: 'fa-hourglass-half',
            rotuloConfirmar: 'Salvar',
            corpoHtml: `
                <label>Minutos sem internet
                    <input type="number" min="1" max="${DURACAO_OFFLINE_MAXIMA_MINUTOS}" step="1" data-campo="tolerancia-minutos"
                        value="${atual === null ? '' : atual}">
                </label>
                <p class="pa-ajuda" style="margin: 0;"><b>${DURACAO_OFFLINE_PADRAO_MINUTOS}</b> = padrão da Neurotec;
                    <b>1440</b> = 24 horas. ${duracoes.length > 1 ? 'As licenças selecionadas têm valores diferentes hoje. ' : ''}
                    Informe o que a Neurotec emitiu para estas licenças: mudar aqui não muda a licença, só o que o app mostra ao
                    operador sobre quanto tempo resta sem internet.</p>
            `,
            coletar: (corpo) => {
                const texto = (corpo.querySelector('[data-campo="tolerancia-minutos"]') as HTMLInputElement).value.trim();
                if (!/^\d+$/.test(texto) || Number(texto) < 1 || Number(texto) > DURACAO_OFFLINE_MAXIMA_MINUTOS) {
                    throw new Error(`Informe a tolerância em minutos, de 1 a ${DURACAO_OFFLINE_MAXIMA_MINUTOS}.`);
                }

                return { duracao_offline_minutos: Number(texto) };
            },
        });
        if (!valores) {
            return;
        }

        try {
            const resposta = await api.post<{ total: number }>(ROTA + 'tolerancia_offline', {}, {
                licencas: [].concat(...pares.map(p => p.licencas.map(l => l.licenca))),
                duracao_offline_minutos: valores.duracao_offline_minutos,
            });
            this.selecionados = {};
            this.aviso = `Tolerância offline de ${formatarDuracaoMinutos(valores.duracao_offline_minutos)} `
                + `aplicada (${resposta.total} arquivo(s) alterado(s)).`;
            await this.carregar();
        } catch (e) {
            await dialogo.avisar('Não foi possível alterar a tolerância offline', e.message);
        }
    }

    private async editarContrato(tenant: number): Promise<void> {
        const cliente = this.resumo ? this.resumo.tenants.find(t => t.tenant === tenant) : null;
        if (!cliente) {
            return;
        }

        const valores = await dialogo.formulario<{ contratadas: number; atualizacao_automatica: boolean }>({
            titulo: `Contrato de ${cliente.tenant_codigo || tenant}`,
            icone: 'fa-file-signature',
            rotuloConfirmar: 'Salvar',
            corpoHtml: `
                <label>Licenças contratadas
                    <input type="number" min="0" step="1" data-campo="contratadas" value="${cliente.contratadas === null ? '' : cliente.contratadas}">
                </label>
                <p class="pa-ajuda" style="margin: 0;">Em uso hoje: <b>${cliente.dispositivos_com_licenca}</b> dispositivo(s).
                    Reduzir abaixo disso não tira licença de ninguém, só impede novas ativações.</p>
                <label class="pa-modal-check">
                    <input type="checkbox" data-campo="automatica" ${cliente.atualizacao_automatica ? 'checked' : ''}>
                    <span><b>Atualizar automaticamente pelo contrato</b><br>
                        <span class="pa-ajuda">Marcada, a sincronização do contrato sobrescreve a quantidade acima.
                        Desmarque para manter um valor definido aqui.</span></span>
                </label>
            `,
            coletar: (corpo) => {
                const texto = (corpo.querySelector('[data-campo="contratadas"]') as HTMLInputElement).value.trim();
                if (!/^\d+$/.test(texto)) {
                    throw new Error('Informe a quantidade de licenças contratadas (número inteiro, 0 ou mais).');
                }

                return {
                    contratadas: Number(texto),
                    atualizacao_automatica: (corpo.querySelector('[data-campo="automatica"]') as HTMLInputElement).checked,
                };
            },
        });
        if (!valores) {
            return;
        }

        try {
            // tenant_id: "tenant" na URL é lido pelo FixedAttributesListener do backend como código de tenant.
            await api.put(ROTA + 'editar_contrato', { tenant_id: tenant }, valores);
            this.aviso = `${cliente.tenant_codigo || tenant}: ${valores.contratadas} licença(s) contratada(s), `
                + (valores.atualizacao_automatica ? 'atualizada(s) pelo contrato.' : 'sem atualização automática.');
            await this.carregar();
        } catch (e) {
            await dialogo.avisar('Não foi possível salvar o contrato', e.message);
        }
    }

    private async liberar(tenant: number, dispositivo: string): Promise<void> {
        const alvo = this.dispositivos.find(d => d.tenant === tenant && d.dispositivo === dispositivo);
        const nome = alvo ? (alvo.nome || alvo.deviceid || dispositivo) : dispositivo;
        const cliente = alvo ? (alvo.tenant_codigo || String(tenant)) : String(tenant);
        const temLicenca = !!(alvo && alvo.licencas.length);

        const resposta = await dialogo.confirmar({
            titulo: temLicenca ? 'Liberar licença' : 'Inativar reconhecimento facial',
            tom: 'alerta',
            pergunta: temLicenca
                ? `Liberar a licença de ${nome} (${cliente})?`
                : `Inativar o reconhecimento facial de ${nome} (${cliente})?`,
            explicacaoHtml: temLicenca
                ? '<p>A licença volta para o estoque e o reconhecimento facial do dispositivo fica inativo. Para voltar a usar, marque <b>Reconhecimento facial ativo</b> no dispositivo, no PontoWeb.</p>'
                : '<p>O dispositivo não tem licença. O reconhecimento facial fica inativo; para voltar a usar, marque <b>Reconhecimento facial ativo</b> no dispositivo, no PontoWeb.</p>',
            campoTexto: { rotulo: 'Motivo (opcional)', placeholder: 'Ex.: sem uso há 30 dias' },
            rotuloConfirmar: temLicenca ? 'Liberar licença' : 'Inativar',
            iconeConfirmar: temLicenca ? 'fa-unlock' : 'fa-power-off',
        });
        if (!resposta.confirmado) {
            return;
        }

        try {
            const resultado = await api.post<{ licencas_liberadas: number; recfacial_desativado: boolean }>(
                ROTA + 'liberar_dispositivo',
                { dispositivo },
                // tenant_id: "tenant" no corpo também é lido pelo FixedAttributesListener como código de tenant.
                { tenant_id: tenant, motivo: resposta.texto || null }
            );
            this.aviso = `${nome} (${cliente}): `
                + (resultado.licencas_liberadas ? 'licença devolvida ao estoque' : 'sem licença para devolver')
                + (resultado.recfacial_desativado ? ' e reconhecimento facial inativado.' : '.');
            await this.carregar();
        } catch (e) {
            await dialogo.avisar('Não foi possível liberar', e.message);
        }
    }

    private async abrirHistorico(licencaId: string): Promise<void> {
        let licenca: LicencaDoPar | null = null;
        this.pares.forEach(p => p.licencas.forEach(l => {
            if (l.licenca === licencaId) {
                licenca = l;
            }
        }));
        if (!licenca) {
            return;
        }

        try {
            const linhas = await api.get<Historico[]>(ROTA + 'historico', { licenca: licencaId });
            this.historico = { licenca, linhas };
            this.renderConteudo();
        } catch (e) {
            await dialogo.avisar('Não foi possível abrir o histórico', e.message);
        }
    }

    private async lerArquivos(input: HTMLInputElement): Promise<void> {
        const arquivos = input.files ? Array.prototype.slice.call(input.files) as File[] : [];

        for (let i = 0; i < arquivos.length; i++) {
            const arquivo = arquivos[i];
            const conteudo = normalizarConteudoLicenca(await this.lerTexto(arquivo));
            const pelonome = interpretarNomeLicenca(arquivo.name, this.valores());
            this.itensCadastro.push({
                nome_arquivo: arquivo.name,
                conteudo,
                componente: pelonome.componente || '',
                tipo: pelonome.tipo,
                formato: pelonome.formato,
                do_nome: { componente: !!pelonome.componente, tipo: !!pelonome.tipo, formato: !!pelonome.formato },
            });
        }

        input.value = '';
        this.render();
    }

    private lerTexto(arquivo: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const leitor = new FileReader();
            leitor.onload = () => resolve(String(leitor.result || ''));
            leitor.onerror = () => reject(new Error(`Não foi possível ler ${arquivo.name}.`));
            leitor.readAsText(arquivo);
        });
    }

    private valores(): ValoresLicenca {
        return (this.resumo && this.resumo.valores) || VALORES_PADRAO;
    }

    private contagemCadastro(componente: string): number {
        return this.itensCadastro.filter(i => i.componente === componente).length;
    }

    private async salvarCadastro(): Promise<void> {
        const semComponente = this.itensCadastro.filter(i => !i.componente).map(i => i.nome_arquivo);
        if (semComponente.length) {
            await dialogo.avisar(
                'Componente não informado',
                `Escolha na tabela se a licença é CLIENT ou MATCHER: ${semComponente.join(', ')}.`,
                'alerta'
            );
            return;
        }

        if (this.contagemCadastro('CLIENT') !== this.contagemCadastro('MATCHER')) {
            await dialogo.avisar(
                'Licenças sem par',
                `Cada dispositivo usa um par CLIENT + MATCHER. A lista tem ${this.contagemCadastro('CLIENT')} CLIENT e `
                + `${this.contagemCadastro('MATCHER')} MATCHER: inclua ou remova arquivos até as quantidades baterem.`,
                'alerta'
            );
            return;
        }

        // o campo numérico devolve '' para o que não consegue ler ("1e", "24h"): sem isso, viraria o padrão em silêncio
        const campoDuracao = this.container.querySelector('[data-campo="duracao-cadastro"]') as HTMLInputElement | null;
        const ilegivel = !!campoDuracao && campoDuracao.validity.badInput;
        const duracao = this.duracaoCadastro.trim();
        if (ilegivel || (duracao && (!/^\d+$/.test(duracao) || Number(duracao) < 1 || Number(duracao) > DURACAO_OFFLINE_MAXIMA_MINUTOS))) {
            await dialogo.avisar(
                'Tolerância offline inválida',
                `Informe a tolerância em minutos, de 1 a ${DURACAO_OFFLINE_MAXIMA_MINUTOS}, ou deixe vazio para o padrão de ${DURACAO_OFFLINE_PADRAO_MINUTOS}.`,
                'alerta'
            );
            return;
        }

        this.salvando = true;
        this.render();

        try {
            const resposta = await api.post<{ total: number }>(ROTA + 'post', {}, {
                licencas: this.itensCadastro.map(item => ({
                    nome_arquivo: item.nome_arquivo,
                    conteudo: item.conteudo,
                    componente: item.componente,
                    tipo: item.tipo,
                    formato: item.formato,
                    data_compra: this.dataCompra || null,
                    duracao_offline_minutos: duracao ? Number(duracao) : null,
                })),
            });
            this.itensCadastro = [];
            // a data da compra costuma valer para o próximo lote; a tolerância não, e herdá-la passaria despercebido
            this.duracaoCadastro = '';
            this.salvando = false;
            this.cadastrando = false;
            this.aviso = `${resposta.total / 2} licença(s) (${resposta.total} arquivos) cadastrada(s) no estoque.`;
            await this.carregar();
        } catch (e) {
            this.salvando = false;
            this.render();
            await dialogo.avisar('Nenhuma licença foi cadastrada', e.message);
        }
    }

    // ---------------------------------------------------------------- eventos

    private aoClicar(evento: MouseEvent): void {
        const alvo = (evento.target as HTMLElement).closest('[data-acao]') as HTMLElement | null;
        if (!alvo) {
            return;
        }

        // a dica tem tratamento próprio: dentro de um cabeçalho ordenável, o clique nela não ordena
        if (alvo.getAttribute('data-acao') === 'ordenar' && (evento.target as HTMLElement).closest('[data-dica]')) {
            return;
        }

        switch (alvo.getAttribute('data-acao')) {
            case 'ordenar': {
                const tabela = alvo.getAttribute('data-tabela') as Tabela;
                this.ordenacao[tabela] = alternarOrdenacao(this.ordenacao[tabela], alvo.getAttribute('data-coluna'));
                this.renderConteudo();
                break;
            }
            case 'recarregar':
                if (this.carregando) {
                    break;
                }
                this.aviso = '';
                this.carregar(true);
                break;
            case 'calcular-uso':
                this.calcularUso();
                break;
            case 'editar-contrato':
                this.editarContrato(Number(alvo.getAttribute('data-tenant')));
                break;
            case 'liberar':
                this.liberar(Number(alvo.getAttribute('data-tenant')), alvo.getAttribute('data-dispositivo'));
                break;
            case 'historico':
                this.abrirHistorico(alvo.getAttribute('data-licenca'));
                break;
            case 'reservar':
                this.reservar();
                break;
            case 'remover-reserva':
                this.removerReserva();
                break;
            case 'bloquear':
                this.bloquear();
                break;
            case 'desbloquear':
                this.desbloquear();
                break;
            case 'tolerancia-offline':
                this.definirToleranciaOffline();
                break;
            case 'limpar-selecao':
                this.selecionados = {};
                this.renderConteudo();
                break;
            case 'fechar-historico':
                this.historico = null;
                this.renderConteudo();
                break;
            case 'abrir-cadastro':
                this.cadastrando = true;
                this.aviso = '';
                this.historico = null;
                this.render();
                break;
            case 'fechar-cadastro':
                this.cadastrando = false;
                this.render();
                break;
            case 'remover-item':
                this.itensCadastro.splice(Number(alvo.getAttribute('data-indice')), 1);
                this.render();
                break;
            case 'salvar-cadastro':
                this.salvarCadastro();
                break;
            case 'limpar-cadastro':
                this.itensCadastro = [];
                this.render();
                break;
        }
    }

    private aoDigitar(evento: Event): void {
        const alvo = evento.target as HTMLInputElement;
        if (alvo.getAttribute('data-campo') === 'busca') {
            this.busca = alvo.value;
            this.renderConteudo();

            // A busca fica dentro do trecho redesenhado: devolve o foco e o cursor.
            const novo = this.container.querySelector('[data-campo="busca"]') as HTMLInputElement | null;
            if (novo) {
                novo.focus();
                novo.setSelectionRange(novo.value.length, novo.value.length);
            }
        }
    }

    private aoMudar(evento: Event): void {
        const alvo = evento.target as HTMLInputElement;

        switch (alvo.getAttribute('data-campo')) {
            case 'dias':
                this.dias = Number(alvo.value);
                this.uso = null;
                this.render();
                break;
            case 'situacao-par':
                this.filtroSituacaoPar = alvo.value;
                this.renderConteudo();
                break;
            case 'reserva-par':
                this.filtroReserva = alvo.value;
                this.renderConteudo();
                break;
            case 'selecionar-par':
                this.selecionados[alvo.getAttribute('data-chave')] = alvo.checked;
                this.renderConteudo();
                break;
            case 'selecionar-todos':
                this.paresVisiveis().forEach(p => this.selecionados[this.chavePar(p)] = alvo.checked);
                this.renderConteudo();
                break;
            case 'arquivos':
                this.lerArquivos(alvo);
                break;
            case 'componente-item':
            case 'tipo-item':
            case 'formato-item': {
                const item = this.itensCadastro[Number(alvo.getAttribute('data-indice'))];
                const campo = alvo.getAttribute('data-campo').replace('-item', '') as 'componente' | 'tipo' | 'formato';
                if (campo === 'componente') {
                    item.componente = alvo.value;
                } else {
                    item[campo] = alvo.value || null;
                }
                item.do_nome[campo] = false;
                this.render();
                break;
            }
            case 'data-compra':
                this.dataCompra = alvo.value;
                break;
            case 'duracao-cadastro':
                this.duracaoCadastro = alvo.value;
                break;
        }
    }

    // ---------------------------------------------------------------- renderização

    private render(): void {
        this.container.innerHTML = `
            ${this.cadastrando ? '' : this.renderCards()}
            ${this.aviso ? mensagem(this.aviso, 'sucesso') : ''}
            ${this.erro ? mensagem(this.erro, 'erro') : ''}
            <div data-conteudo></div>
        `;

        this.renderConteudo();
    }

    private renderConteudo(): void {
        const alvo = this.container.querySelector('[data-conteudo]') as HTMLElement;
        if (!alvo) {
            return;
        }

        if (this.carregando && !this.carregado) {
            alvo.innerHTML = '<div class="pa-vazio">Carregando…</div>';
            return;
        }

        switch (this.secao) {
            case 'clientes':
                alvo.innerHTML = this.renderClientes();
                break;
            case 'dispositivos':
                alvo.innerHTML = this.renderDispositivos();
                break;
            case 'licencas':
                alvo.innerHTML = this.cadastrando ? this.renderCadastro() : this.renderLicencas();
                break;
        }
    }

    private renderCards(): string {
        if (!this.resumo) {
            return '';
        }

        const disponiveis = this.resumo.estoque.licencas_disponiveis;
        const reservadasLivres = this.resumo.estoque.licencas_reservadas_disponiveis || 0;
        const aguardando = this.resumo.tenants.reduce((total, t) => total + Math.max(t.dispositivos_aguardando_licenca, 0), 0);
        const card = (tom: string, icone: string, titulo: string, numero: number, detalhe: string) => `
            <div class="pa-card pa-card--${tom}">
                <span class="pa-card-icone"><i class="fas ${icone}"></i></span>
                <div>
                    <h3>${escaparHtml(titulo)}</h3>
                    <div class="pa-numero">${numero}</div>
                    <div class="pa-detalhe">${escaparHtml(detalhe)}</div>
                </div>
            </div>
        `;

        // Cor = estado: vermelho pede ação, laranja pede atenção, verde está ok.
        return `
            <div class="pa-cards">
                ${card(disponiveis === 0 ? 'vermelho' : disponiveis <= 2 ? 'laranja' : 'verde', 'fa-key',
                    'Licenças disponíveis', disponiveis,
                    (disponiveis === 0 ? `Estoque esgotado: nenhum dispositivo novo consegue licença. ${TEXTO_LICENCAS_DISPONIVEIS}` : TEXTO_LICENCAS_DISPONIVEIS)
                    + (reservadasLivres ? ` Além delas, ${reservadasLivres} livre(s) reservada(s) a clientes.` : ''))}
                ${card(aguardando > 0 ? 'laranja' : 'verde', 'fa-hourglass-half', 'Dispositivos aguardando licença', aguardando, TEXTO_AGUARDANDO)}
            </div>
        `;
    }

    private renderBusca(placeholder: string): string {
        return `<input type="text" data-campo="busca" placeholder="${escaparHtml(placeholder)}" value="${escaparHtml(this.busca)}" style="min-width: 280px;">`;
    }

    private botaoAtualizar(): string {
        return `
            <button class="pa-btn pa-btn--secundario" data-acao="recarregar" ${this.carregando ? 'disabled' : ''}>
                <i class="fas fa-sync-alt ${this.carregando ? 'fa-spin' : ''}" style="animation-duration: ${DURACAO_GIRO_MS}ms;"></i>${this.carregando ? 'Atualizando…' : 'Atualizar'}
            </button>
        `;
    }

    private renderClientes(): string {
        const colunas: ColunaOrdenavel<Cliente>[] = [
            { chave: 'tenant', rotulo: 'Tenant', valor: t => t.tenant, classe: 'pa-num' },
            { chave: 'codigo', rotulo: 'Código', valor: t => t.tenant_codigo },
            { chave: 'contratadas', rotulo: 'Contratadas', valor: t => t.contratadas, classe: 'pa-num' },
            { chave: 'automatica', rotulo: 'Atualização', valor: t => t.atualizacao_automatica },
            { chave: 'recfacial', rotulo: 'Rec. facial ativo', valor: t => t.dispositivos_recfacial, classe: 'pa-num' },
            { chave: 'comlicenca', rotulo: 'Com licença', valor: t => t.dispositivos_com_licenca, classe: 'pa-num' },
            { chave: 'aguardando', rotulo: 'Aguardando', valor: t => t.dispositivos_aguardando_licenca, classe: 'pa-num' },
            { chave: 'saldo', rotulo: 'Saldo do contrato', valor: t => t.saldo_contrato, classe: 'pa-num' },
            { chave: 'reservadas', rotulo: 'Reservadas', valor: t => t.licencas_reservadas, classe: 'pa-num' },
        ];
        const linhas = ordenarLinhas(
            this.resumo.tenants.filter(t => contemTexto(this.busca, t.tenant, t.tenant_codigo, t.tenant_nome)),
            colunas,
            this.ordenacao.clientes
        );

        return `
            <div class="pa-toolbar">
                ${this.renderBusca('Buscar tenant (código, nome ou número)')}
                <span class="pa-espaco"></span>
                ${this.botaoAtualizar()}
            </div>
            <div class="pa-tabela-wrap"><table>
                <thead>${cabecalhoOrdenavel('clientes', colunas, this.ordenacao.clientes, '<th></th>')}</thead>
                <tbody>
                    ${linhas.length ? linhas.map(t => `
                        <tr>
                            <td class="pa-num">${t.tenant}</td>
                            <td>${escaparHtml(t.tenant_codigo)}${t.tenant_nome ? `<div class="pa-mono">${escaparHtml(t.tenant_nome)}</div>` : ''}</td>
                            <td class="pa-num">${t.contratadas === null ? badge('sem configuração', 'alerta') : t.contratadas}</td>
                            <td>${t.atualizacao_automatica ? badge('Pelo contrato', 'neutro') : badge('Manual', 'alerta')}</td>
                            <td class="pa-num">${t.dispositivos_recfacial}</td>
                            <td class="pa-num">${t.dispositivos_com_licenca}</td>
                            <td class="pa-num">${t.dispositivos_aguardando_licenca > 0 ? badge(String(t.dispositivos_aguardando_licenca), 'alerta') : t.dispositivos_aguardando_licenca}</td>
                            <td class="pa-num">${t.saldo_contrato < 0 ? badge(String(t.saldo_contrato), 'erro') : t.saldo_contrato}</td>
                            <td class="pa-num">${t.licencas_reservadas
                                ? `${t.licencas_reservadas} <span class="pa-mono">(${t.licencas_reservadas_livres} livre${t.licencas_reservadas_livres === 1 ? '' : 's'})</span>`
                                : '—'}</td>
                            <td><button class="pa-btn pa-btn--secundario" data-acao="editar-contrato" data-tenant="${t.tenant}"><i class="fas fa-pen"></i>Editar</button></td>
                        </tr>
                    `).join('') : '<tr><td colspan="10" class="pa-vazio">Nenhum cliente encontrado.</td></tr>'}
                </tbody>
            </table></div>
        `;
    }

    private usoDe(d: Dispositivo): Uso | null {
        return this.uso ? this.uso[`${d.tenant}|${d.dispositivo}`] || null : null;
    }

    private renderDispositivos(): string {
        const colunas: ColunaOrdenavel<Dispositivo>[] = [
            { chave: 'tenant', rotulo: 'Tenant', valor: d => d.tenant_codigo },
            { chave: 'dispositivo', rotulo: 'Dispositivo', valor: d => d.nome || d.deviceid },
            { chave: 'situacao', rotulo: 'Situação', valor: d => (SITUACAO_DISPOSITIVO[d.situacao] || [d.situacao])[0] },
            { chave: 'licencas', rotulo: 'Licença', valor: d => d.licencas.length ? d.licencas[0].nome_arquivo : null },
            { chave: 'desde', rotulo: 'Licença desde', valor: d => d.data_ativacao_licenca },
            {
                chave: 'tolerancia', rotulo: 'Tolerância offline', classe: 'pa-num', dica: dica(TEXTO_TOLERANCIA_DISPOSITIVO),
                valor: d => d.duracao_offline_minutos
            },
            { chave: 'contato', rotulo: 'Último contato', dica: dica(TEXTO_ULTIMO_CONTATO), valor: d => d.data_ultimo_contato },
            {
                chave: 'uso', rotulo: 'Última marcação (rec. facial)', valor: d => {
                    const uso = this.usoDe(d);
                    return uso ? uso.ultima_marcacao_recfacial : null;
                }
            },
            {
                chave: 'marcacoes', rotulo: 'Marcações', classe: 'pa-num', valor: d => {
                    const uso = this.usoDe(d);
                    return this.uso ? (uso ? uso.marcacoes_recfacial : 0) : null;
                }
            },
        ];
        const filtradas = this.dispositivos.filter(d => contemTexto(this.busca, d.tenant, d.tenant_codigo, d.nome, d.deviceid, d.dispositivo));
        const linhas = this.ordenacao.dispositivos
            ? ordenarLinhas(filtradas, colunas, this.ordenacao.dispositivos)
            : filtradas.slice().sort((a, b) => this.ordemPadraoDispositivo(a) - this.ordemPadraoDispositivo(b));

        return `
            <div class="pa-toolbar">
                ${this.renderBusca('Buscar tenant, dispositivo ou device id')}
                <label class="pa-ajuda" style="margin: 0;">Marcações dos últimos
                    <select data-campo="dias">
                        ${[7, 15, 30, 60, 90].map(d => `<option value="${d}" ${d === this.dias ? 'selected' : ''}>${d} dias</option>`).join('')}
                    </select>
                </label>
                <button class="pa-btn" data-acao="calcular-uso" ${this.calculandoUso ? 'disabled' : ''}>
                    <i class="fas ${this.calculandoUso ? 'fa-spinner fa-spin' : 'fa-chart-line'}"></i>${this.calculandoUso ? 'Calculando…' : 'Calcular última marcação'}
                </button>
                <span class="pa-espaco"></span>
                ${this.botaoAtualizar()}
            </div>
            <p class="pa-ajuda">A última marcação considera só as marcações por reconhecimento facial e é uma consulta pesada: calcule quando precisar decidir o que liberar.
                Sem ordenação escolhida, os dispositivos com licença e sem marcação no período aparecem primeiro.</p>
            ${this.erroUso ? mensagem(this.erroUso, 'erro') : ''}
            <div class="pa-tabela-wrap"><table>
                <thead>${cabecalhoOrdenavel('dispositivos', colunas, this.ordenacao.dispositivos, '<th></th>')}</thead>
                <tbody>
                    ${linhas.length ? linhas.map(d => this.renderLinhaDispositivo(d)).join('') : '<tr><td colspan="10" class="pa-vazio">Nenhum dispositivo encontrado.</td></tr>'}
                </tbody>
            </table></div>
        `;
    }

    private renderLinhaDispositivo(d: Dispositivo): string {
        const [rotulo, tom] = SITUACAO_DISPOSITIVO[d.situacao] || [d.situacao, 'neutro' as Tom];
        const uso = this.usoDe(d);

        let ultimoUso = '<span class="pa-mono">não calculado</span>';
        let marcacoes = '—';
        if (this.uso) {
            if (uso) {
                const dias = diasDesde(uso.ultima_marcacao_recfacial);
                ultimoUso = `${formatarDataHora(uso.ultima_marcacao_recfacial)} <span class="pa-mono">(${dias === null ? '?' : dias} d)</span>`;
                marcacoes = String(uso.marcacoes_recfacial);
            } else {
                ultimoUso = badge(`sem marcação em ${this.dias} dias`, d.licencas.length ? 'erro' : 'neutro');
                marcacoes = '0';
            }
        }

        const podeLiberar = d.licencas.length > 0 || (d.usarrecfacialativo && !d.excluido);
        const contato = tempoDesde(d.data_ultimo_contato);

        return `
            <tr>
                <td>${escaparHtml(d.tenant_codigo)} <span class="pa-mono">${d.tenant}</span></td>
                <td>${escaparHtml(d.nome || '(sem nome)')}
                    <div class="pa-mono">${escaparHtml(d.deviceid || d.dispositivo)}</div>
                    ${d.excluido ? badge('excluído', 'erro') : ''}${!d.usarrecfacialativo ? badge('rec. facial inativo', 'neutro') : ''}
                </td>
                <td>${badge(rotulo, tom)}</td>
                <td>${d.licencas.map(l => `<div class="pa-mono">${escaparHtml(l.componente)}: ${escaparHtml(l.nome_arquivo)}</div>`).join('') || '—'}</td>
                <td>${formatarDataHora(d.data_ativacao_licenca)}</td>
                <td class="pa-num">${formatarDuracaoMinutos(d.duracao_offline_minutos)}</td>
                <td>${formatarDataHora(d.data_ultimo_contato)}${contato ? ` <span class="pa-mono">(${contato})</span>` : ''}</td>
                <td>${ultimoUso}</td>
                <td class="pa-num">${marcacoes}</td>
                <td>${podeLiberar ? `<button class="pa-btn pa-btn--perigo" data-acao="liberar" data-tenant="${d.tenant}" data-dispositivo="${escaparHtml(d.dispositivo)}">
                    <i class="fas ${d.licencas.length ? 'fa-unlock' : 'fa-power-off'}"></i>${d.licencas.length ? 'Liberar licença' : 'Inativar rec. facial'}</button>` : ''}</td>
            </tr>
        `;
    }

    /** Sem ordenação escolhida: com o uso calculado, licença sem uso primeiro; sem ele, licença presa primeiro. */
    private ordemPadraoDispositivo(d: Dispositivo): number {
        if (this.uso && d.licencas.length) {
            const uso = this.usoDe(d);
            if (!uso) {
                return -1e12;
            }

            return -(diasDesde(uso.ultima_marcacao_recfacial) || 0);
        }

        const pesos = { LICENCA_SEM_USO_POSSIVEL: 0, COM_LICENCA: 1, AGUARDANDO_LICENCA: 2 };

        return (pesos[d.situacao] !== undefined ? pesos[d.situacao] : 3) * 1e6;
    }

    private chavePar(p: Par): string {
        return p.licencas.map(l => l.licenca).join('|');
    }

    private paresVisiveis(): Par[] {
        return this.pares.filter(p =>
            (!this.filtroSituacaoPar || p.situacao === this.filtroSituacaoPar)
            && (!this.filtroReserva
                || (this.filtroReserva === 'sem' ? p.tenant_reservado === null : String(p.tenant_reservado) === this.filtroReserva))
            && contemTexto(this.busca, p.tenant, p.tenant_codigo, p.dispositivo_nome, p.deviceid, p.tenant_reservado_codigo,
                ...p.licencas.map(l => l.nome_arquivo), ...p.licencas.map(l => l.licenca))
        );
    }

    private paresSelecionados(): Par[] {
        return this.pares.filter(p => this.selecionados[this.chavePar(p)]);
    }

    private renderLicencas(): string {
        const colunas: ColunaOrdenavel<Par>[] = [
            { chave: 'situacao', rotulo: 'Situação', valor: p => (SITUACAO_PAR[p.situacao] || [p.situacao])[0] },
            { chave: 'reserva', rotulo: 'Reservada para', valor: p => p.tenant_reservado_codigo },
            { chave: 'tenant', rotulo: 'Tenant', valor: p => p.tenant_codigo },
            { chave: 'dispositivo', rotulo: 'Dispositivo', valor: p => p.dispositivo_nome || p.deviceid },
            { chave: 'licenca', rotulo: 'Licença', valor: p => p.licencas.length ? p.licencas[0].nome_arquivo : null },
            { chave: 'tipo', rotulo: 'Tipo · formato', valor: p => this.tipoFormatoDoPar(p) },
            {
                chave: 'tolerancia', rotulo: 'Tolerância offline', classe: 'pa-num', dica: dica(TEXTO_TOLERANCIA_LICENCAS),
                valor: p => p.duracao_offline_minutos
            },
            { chave: 'vinculada', rotulo: 'Em uso desde', valor: p => p.data_ativacao },
            { chave: 'compra', rotulo: 'Compra', valor: p => p.data_compra },
        ];
        const visiveis = this.paresVisiveis();
        const linhas = ordenarLinhas(visiveis, colunas, this.ordenacao.licencas);
        const totalPorSituacao = (s: string) => this.pares.filter(p => p.situacao === s).length;
        const selecionados = this.paresSelecionados();
        const todosMarcados = visiveis.length > 0 && visiveis.every(p => this.selecionados[this.chavePar(p)]);
        const clientesComReserva: { [tenant: string]: string } = {};
        this.pares.forEach(p => {
            if (p.tenant_reservado !== null) {
                clientesComReserva[p.tenant_reservado] = p.tenant_reservado_codigo || String(p.tenant_reservado);
            }
        });

        return `
            <div class="pa-toolbar">
                ${this.renderBusca('Buscar arquivo, tenant ou dispositivo')}
                <select data-campo="situacao-par">
                    <option value="">Todas as situações</option>
                    ${Object.keys(SITUACAO_PAR).map(s => `<option value="${s}" ${s === this.filtroSituacaoPar ? 'selected' : ''}>${SITUACAO_PAR[s][0]} (${totalPorSituacao(s)})</option>`).join('')}
                </select>
                <select data-campo="reserva-par">
                    <option value="">Com e sem reserva</option>
                    <option value="sem" ${this.filtroReserva === 'sem' ? 'selected' : ''}>Sem reserva</option>
                    ${Object.keys(clientesComReserva).map(t => `<option value="${escaparHtml(t)}" ${t === this.filtroReserva ? 'selected' : ''}>Reservadas para ${escaparHtml(clientesComReserva[t])}</option>`).join('')}
                </select>
                <span class="pa-espaco"></span>
                <span class="pa-ajuda" style="margin: 0;">${linhas.length} licença(s)
                    ${dica('Cada licença é um par CLIENT + MATCHER, a unidade que um dispositivo usa. O número conta as licenças da grade, com busca e filtro aplicados. O par disponível nº 1 é o próximo a ser entregue. Licença reservada só é usada pelo cliente da reserva, e esse cliente só usa as reservadas a ele.')}</span>
                ${this.botaoAtualizar()}
                <button class="pa-btn" data-acao="abrir-cadastro"><i class="fas fa-plus-circle"></i>Cadastrar licenças</button>
            </div>
            ${selecionados.length ? `
                <div class="pa-selecao">
                    <b>${selecionados.length} licença(s) selecionada(s)</b>
                    <button class="pa-btn" data-acao="reservar"><i class="fas fa-user-lock"></i>Reservar para cliente…</button>
                    <button class="pa-btn pa-btn--secundario" data-acao="remover-reserva" ${selecionados.some(p => p.tenant_reservado !== null) ? '' : 'disabled'}><i class="fas fa-lock-open"></i>Remover reserva</button>
                    <button class="pa-btn pa-btn--secundario" data-acao="bloquear" ${selecionados.every(p => p.situacao === 'DISPONIVEL') ? '' : 'disabled title="Só licença disponível pode ser bloqueada. Para bloquear uma em uso, libere o dispositivo antes."'}><i class="fas fa-ban"></i>Bloquear…</button>
                    <button class="pa-btn pa-btn--secundario" data-acao="desbloquear" ${selecionados.every(p => p.situacao === 'BLOQUEADA') ? '' : 'disabled title="Só licença bloqueada pode ser desbloqueada."'}><i class="fas fa-unlock"></i>Desbloquear</button>
                    <button class="pa-btn pa-btn--secundario" data-acao="tolerancia-offline"><i class="fas fa-hourglass-half"></i>Tolerância offline…</button>
                    ${dica(TEXTO_TOLERANCIA_OFFLINE)}
                    <button class="pa-btn pa-btn--link" data-acao="limpar-selecao">Limpar seleção</button>
                </div>` : ''}
            <div class="pa-tabela-wrap"><table>
                <thead>${cabecalhoOrdenavel('licencas', colunas, this.ordenacao.licencas).replace('<tr>',
                    `<tr><th class="pa-col-selecao"><input type="checkbox" data-campo="selecionar-todos" ${todosMarcados ? 'checked' : ''} aria-label="Selecionar todas"></th>`)}</thead>
                <tbody>
                    ${linhas.length ? linhas.map(p => this.renderPar(p)).join('') : '<tr><td colspan="10" class="pa-vazio">Nenhuma licença encontrada.</td></tr>'}
                </tbody>
            </table></div>
            ${this.historico ? this.renderHistorico() : ''}
        `;
    }

    /** Uma linha por par, como na grade de dispositivos: CLIENT e MATCHER juntos na coluna Licença. */
    private renderPar(p: Par): string {
        const [rotulo, tom] = SITUACAO_PAR[p.situacao] || [p.situacao, 'neutro' as Tom];
        const emFoco = p.licencas.some(l => this.historico && this.historico.licenca.licenca === l.licenca);
        const faltando = this.valores().componentes.filter(c => !p.licencas.some(l => l.componente === c));
        const chave = this.chavePar(p);

        return `
            <tr class="${emFoco ? 'pa-linha--foco' : ''} ${this.selecionados[chave] ? 'pa-linha--selecionada' : ''}">
                <td class="pa-col-selecao"><input type="checkbox" data-campo="selecionar-par" data-chave="${escaparHtml(chave)}"
                    ${this.selecionados[chave] ? 'checked' : ''} ${p.completo ? '' : 'disabled title="Par incompleto: não pode ser reservado nem bloqueado"'}></td>
                <td>${badge(rotulo, tom)}
                    ${p.situacao === 'DISPONIVEL' && p.ordem === 1 && p.tenant_reservado === null ? `<div>${badge('próxima a ser usada', 'ok')}</div>` : ''}
                    ${!p.completo ? `<div>${badge(`sem ${faltando.join(', ')}`, 'erro')}</div>` : ''}</td>
                <td>${p.tenant_reservado !== null
                    ? `${badge(p.tenant_reservado_codigo || String(p.tenant_reservado), 'alerta')} <span class="pa-mono">${p.tenant_reservado}</span>`
                    : '—'}</td>
                <td>${p.tenant !== null ? `${escaparHtml(p.tenant_codigo)} <span class="pa-mono">${p.tenant}</span>` : '—'}</td>
                <td>${p.dispositivo ? `${escaparHtml(p.dispositivo_nome || '(sem nome)')}<div class="pa-mono">${escaparHtml(p.deviceid || p.dispositivo)}</div>` : '—'}</td>
                <td>${p.licencas.map(l => `
                    <div class="pa-mono pa-licenca-linha">${escaparHtml(l.componente)}: ${escaparHtml(l.nome_arquivo)}
                        <button class="pa-btn pa-btn--link pa-licenca-historico" data-acao="historico" data-licenca="${escaparHtml(l.licenca)}"
                            title="Histórico de ${escaparHtml(l.nome_arquivo)}" aria-label="Histórico de ${escaparHtml(l.nome_arquivo)}"><i class="fas fa-history"></i></button>
                    </div>`).join('')}</td>
                <td>${escaparHtml(this.tipoFormatoDoPar(p))}</td>
                <td class="pa-num">${this.toleranciaDoPar(p)}</td>
                <td>${formatarDataHora(p.data_ativacao)}</td>
                <td>${formatarDataHora(p.data_compra)}</td>
            </tr>
        `;
    }

    /** Tipo e formato do par numa linha; se os dois arquivos divergirem, mostra os dois. */
    private tipoFormatoDoPar(p: Par): string {
        return distintos(p.licencas.map(l => `${l.tipo} · ${l.formato}`)).join(' / ');
    }

    /** A tolerância do par é a menor; se os dois arquivos divergirem, mostra os dois. */
    private toleranciaDoPar(p: Par): string {
        if (distintos(p.licencas.map(l => l.duracao_offline_minutos)).length <= 1) {
            return formatarDuracaoMinutos(p.duracao_offline_minutos);
        }

        return `${formatarDuracaoMinutos(p.duracao_offline_minutos)}
            <div class="pa-mono">${p.licencas.map(l => `${escaparHtml(l.componente)} ${formatarDuracaoMinutos(l.duracao_offline_minutos)}`).join(' · ')}</div>`;
    }

    private renderHistorico(): string {
        const { licenca, linhas } = this.historico;
        const tomAcao = (acao: string) => acao === 'ATIVACAO' ? 'ok' : acao === 'DESATIVACAO' || acao === 'BLOQUEIO' ? 'alerta' : 'neutro';
        const rotuloAcao: { [acao: string]: string } = {
            CADASTRO: 'Cadastrada',
            ATIVACAO: 'Vinculada ao dispositivo',
            DESATIVACAO: 'Devolvida ao estoque',
            SINCRONIZACAO: 'Sincronizada',
            TOLERANCIA_OFFLINE: 'Tolerância offline alterada',
            BLOQUEIO: 'Bloqueada',
            DESBLOQUEIO: 'Desbloqueada',
            RESERVA: 'Reservada para cliente',
            RESERVA_REMOVIDA: 'Reserva removida',
        };

        return `
            <div class="pa-drawer-fundo" data-acao="fechar-historico"></div>
            <aside class="pa-drawer" role="dialog" aria-label="Histórico da licença">
                <header class="pa-drawer-cabecalho">
                    <div>
                        <div class="pa-drawer-titulo"><i class="fas fa-history"></i> Histórico da licença</div>
                        <div class="pa-mono">${escaparHtml(licenca.nome_arquivo)}</div>
                        <div style="margin-top: 6px;">${badge(licenca.componente, 'neutro')}</div>
                    </div>
                    <button class="pa-drawer-fechar" data-acao="fechar-historico" aria-label="Fechar">×</button>
                </header>
                <div class="pa-drawer-corpo">
                    ${linhas.length ? `<ol class="pa-timeline">
                        ${linhas.map(h => `
                            <li class="pa-timeline-item pa-timeline-item--${tomAcao(h.acao)}">
                                <div class="pa-timeline-data">${formatarDataHora(h.data_evento)}</div>
                                <div class="pa-timeline-acao">${escaparHtml(rotuloAcao[h.acao] || h.acao)}</div>
                                ${h.dispositivo || h.tenant !== null ? `<div class="pa-timeline-alvo">
                                    ${escaparHtml(h.dispositivo_nome || h.dispositivo || '')}
                                    ${h.tenant !== null ? `<span class="pa-mono">· ${escaparHtml(h.tenant_codigo)} (${h.tenant})</span>` : ''}
                                </div>` : ''}
                                ${h.detalhe ? `<div class="pa-timeline-detalhe">${escaparHtml(h.detalhe)}</div>` : ''}
                            </li>
                        `).join('')}
                    </ol>` : '<div class="pa-vazio">Sem histórico.</div>'}
                </div>
            </aside>
        `;
    }

    private renderCadastro(): string {
        const valores = this.valores();
        const opcoes = (lista: string[], selecionado: string | null, vazio: string) => `
            <option value="" ${!selecionado ? 'selected' : ''}>${escaparHtml(vazio)}</option>
            ${lista.map(v => `<option value="${escaparHtml(v)}" ${v === selecionado ? 'selected' : ''}>${escaparHtml(v)}</option>`).join('')}
        `;
        // Sempre editável: a dedução pelo nome só pré-seleciona.
        const campo = (item: ItemCadastro, indice: number, nome: 'componente' | 'tipo' | 'formato', lista: string[], vazio: string) => `
            <select data-campo="${nome}-item" data-indice="${indice}" class="${nome === 'componente' && !item.componente ? 'pa-campo--pendente' : ''}">
                ${opcoes(lista, item[nome], vazio)}
            </select>
            ${item.do_nome[nome] ? '<div class="pa-mono">do nome</div>' : ''}
        `;
        const semComponente = this.itensCadastro.filter(i => !i.componente).length;
        const clients = this.contagemCadastro('CLIENT');
        const matchers = this.contagemCadastro('MATCHER');
        const emPares = clients === matchers;
        const itens = this.itensCadastro
            .map((item, indice) => ({ item, indice }))
            .sort((a, b) => a.item.nome_arquivo.localeCompare(b.item.nome_arquivo, 'pt-BR', { numeric: true }));

        return `
            <div class="pa-toolbar">
                <button class="pa-btn pa-btn--secundario" data-acao="fechar-cadastro"><i class="fas fa-arrow-left"></i>Voltar para licenças</button>
            </div>

            <div class="pa-secao">
                <h2><i class="fas fa-file-upload"></i>1. Arquivos de licença</h2>
                <p class="pa-ajuda">Selecione os arquivos <b>.lic</b> recebidos da Neurotechnology. Cada licença é um par: um arquivo
                    <b>FaceClient</b> e um <b>FaceMatcher</b>. O componente (${escaparHtml(valores.componentes.join('/'))}), o tipo
                    (${escaparHtml(valores.tipos.join(', '))}) e o formato (${escaparHtml(valores.formatos.join(', '))}) são pré-selecionados
                    pelo nome, em qualquer posição — pode manter o seu número de identificação na frente — e podem ser trocados na tabela.
                    O conteúdo é gravado numa linha só, como o app mobile exige.</p>
                <input type="file" multiple accept=".lic" data-campo="arquivos">
            </div>

            <div class="pa-secao">
                <h2><i class="fas fa-clipboard-check"></i>2. Conferir e salvar</h2>
                ${this.itensCadastro.length ? `
                    <div class="pa-toolbar">
                        ${badge(`${clients} CLIENT`, 'neutro')} ${badge(`${matchers} MATCHER`, 'neutro')}
                        ${emPares ? badge(`${clients} licença(s)`, 'ok') : badge('quantidades diferentes: falta o par de alguma licença', 'erro')}
                        ${semComponente ? badge(`${semComponente} sem componente: escolha na tabela`, 'alerta') : ''}
                    </div>` : ''}
                <div class="pa-form">
                    <label>Data da compra<input type="date" data-campo="data-compra" value="${escaparHtml(this.dataCompra)}"></label>
                    <label for="pa-duracao-cadastro"><span id="pa-duracao-cadastro-rotulo">Tolerância offline (minutos)</span>
                        ${dica(TEXTO_TOLERANCIA_OFFLINE)}
                        <input type="number" min="1" max="${DURACAO_OFFLINE_MAXIMA_MINUTOS}" step="1" data-campo="duracao-cadastro"
                            id="pa-duracao-cadastro" aria-labelledby="pa-duracao-cadastro-rotulo"
                            value="${escaparHtml(this.duracaoCadastro)}" placeholder="${DURACAO_OFFLINE_PADRAO_MINUTOS} (padrão)">
                    </label>
                </div>
                <div class="pa-tabela-wrap"><table>
                    <thead><tr><th>Arquivo</th><th>Componente</th><th>Tipo</th><th>Formato</th><th>Conteúdo</th><th></th></tr></thead>
                    <tbody>
                        ${itens.length ? itens.map(({ item, indice }) => `
                            <tr>
                                <td>${escaparHtml(item.nome_arquivo)}</td>
                                <td>${campo(item, indice, 'componente', valores.componentes, 'Escolha…')}</td>
                                <td>${campo(item, indice, 'tipo', valores.tipos, 'Padrão (MOBILE)')}</td>
                                <td>${campo(item, indice, 'formato', valores.formatos, 'Padrão (SERIAL)')}</td>
                                <td class="pa-mono">${escaparHtml(this.resumirConteudo(item.conteudo))}</td>
                                <td><button class="pa-btn pa-btn--link" data-acao="remover-item" data-indice="${indice}">Remover</button></td>
                            </tr>
                        `).join('') : '<tr><td colspan="6" class="pa-vazio">Nenhum arquivo na lista.</td></tr>'}
                    </tbody>
                </table></div>
                <div class="pa-toolbar" style="margin-top: 12px;">
                    <span class="pa-ajuda" style="margin: 0;">O lote é gravado inteiro ou nada: se um arquivo já existir ou faltar par, nenhum é salvo.</span>
                    <span class="pa-espaco"></span>
                    <button class="pa-btn pa-btn--secundario" data-acao="limpar-cadastro" ${!this.itensCadastro.length ? 'disabled' : ''}>Limpar</button>
                    <button class="pa-btn" data-acao="salvar-cadastro" ${!this.itensCadastro.length || !emPares || this.salvando ? 'disabled' : ''}>
                        <i class="fas ${this.salvando ? 'fa-spinner fa-spin' : 'fa-save'}"></i>${this.salvando ? 'Salvando…' : `Cadastrar ${emPares ? clients : ''} licença(s)`}
                    </button>
                </div>
            </div>
        `;
    }

    /** Mostra só as pontas do serial: o conteúdo é a chave da licença. */
    private resumirConteudo(conteudo: string): string {
        if (conteudo.length <= 12) {
            return conteudo;
        }

        return `${conteudo.substr(0, 6)}…${conteudo.substr(-4)} (${conteudo.length} caracteres)`;
    }
}
